<script lang="ts" setup>
import { ChevronDownIcon } from '@heroicons/vue/20/solid';
import { faEnvelope, faPaperPlane } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { type CallbackOption, type GetProjectDetailsProjectResponse, type PreferenceOption, sendMessage } from '@/api';
import { computed, reactive, ref, useId, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { isValidationProblemDetails } from '@/common/api.ts';
import ResponsiveTurnstileWidget from '@/components/ResponsiveTurnstileWidget.vue';

const props = defineProps<{
  project?: GetProjectDetailsProjectResponse;
  header?: string;
}>();

const { t } = useI18n();

// Chaque instance a ses propres identifiants de champs : le formulaire peut donc être monté
// plusieurs fois sur la même page (fiche projet et popup de modèle, par exemple).
const uid = useId();
const fieldId = (name: string) => `${uid}-${name}`;

const showForm = ref(true);
const isSubmitting = ref(false);
const responseError = ref('');

const name = reactive({
  value: '',
  valid: true,
  error: '',
});

const email = reactive({
  value: '',
  valid: true,
  error: '',
});

const telephone = reactive({
  value: '',
  valid: true,
  error: '',
});

const message = reactive({
  value: '',
  valid: true,
  error: '',
});

const callback = reactive({
  value: 'asap',
  valid: true,
  error: '',
});

const preference = reactive({
  value: 'anytime',
  valid: true,
  error: '',
});

const subscribe = reactive({
  value: false,
  valid: true,
  error: '',
});

const token = reactive({
  value: '',
  valid: false,
  error: '',
});

const turnstileWidget = ref<InstanceType<typeof ResponsiveTurnstileWidget> | null>(null);

const resetTurnstile = () => {
  token.value = '';
  turnstileWidget.value?.reset();
};

// Validation logic for individual fields
const isValidEmail = (email: string) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const isValidTelephone = (value: string) => {
  const trimmedValue = value.trim();
  if (!trimmedValue) {
    return true;
  }

  const extensionMatch = trimmedValue.match(/(?:\s*[,.-]?\s*(?:x|p|ext|pst|extension|poste)\s*[:,.-]?\s*)(\d+)\s*$/i);
  const extension = extensionMatch?.[1] ?? '';
  if (extension.length === 1 || extension.length > 10) {
    return false;
  }

  const extensionStartIndex = extensionMatch?.index ?? trimmedValue.length;
  const number = extensionMatch ? trimmedValue.slice(0, extensionStartIndex).trim() : trimmedValue;
  const digits = number.replace(/\D/g, '');
  const normalizedDigits = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;

  if (normalizedDigits.length !== 10) {
    return false;
  }

  const areaCode = normalizedDigits.slice(0, 3);
  const centralOffice = normalizedDigits.slice(3, 6);

  return /^[2-9]\d{2}$/.test(areaCode) && /^[2-9]\d{2}$/.test(centralOffice);
};

const validateName = () => {
  if (name.value.trim().length === 0) {
    name.valid = false;
    name.error = t('components.contact-form.validation.name-required');
  } else {
    name.valid = true;
    name.error = '';
  }
};

const validateEmail = () => {
  if (!email.value.trim()) {
    email.valid = false;
    email.error = t('components.contact-form.validation.email-required');
  } else if (!isValidEmail(email.value)) {
    email.valid = false;
    email.error = t('components.contact-form.validation.email-invalid');
  } else {
    email.valid = true;
    email.error = '';
  }
};

const validateTelephone = () => {
  if (telephone.value.trim() && !isValidTelephone(telephone.value)) {
    telephone.valid = false;
    telephone.error = t('components.contact-form.validation.telephone-invalid');
  } else {
    telephone.valid = true;
    telephone.error = '';
  }
};

const validateToken = () => {
  if (!token.value) {
    token.valid = false;
    token.error = t('components.contact-form.validation.captcha-failed');
  } else {
    token.valid = true;
    token.error = '';
  }
};

const handleTurnstileSuccess = (value: string) => {
  token.value = value;
};

const handleTurnstileError = (error: string) => {
  token.value = '';
  console.error('Turnstile error:', error);
};

const handleTurnstileExpired = () => {
  token.value = '';
};

const validateForm = () => {
  validateName();
  validateEmail();
  validateTelephone();
  validateToken();
};

const isFormValid = computed(() => {
  return name.valid && email.valid && telephone.valid && token.valid;
});

const handleSubmit = async () => {
  if (isSubmitting.value) {
    return;
  }

  responseError.value = '';
  validateForm();
  if (!isFormValid.value) {
    return;
  }

  const bestMatchForCityId = props.project ? props.project.address.city.id : null;
  const bestMatchForRegionId = props.project ? props.project.address.region.id : null;

  isSubmitting.value = true;
  try {
    const result = props.project
      ? await sendMessage({
          query: {
            projectId: props.project.id,
          },
          body: {
            name: name.value,
            email: email.value,
            telephone: telephone.value,
            message: message.value,
            callback: callback.value as CallbackOption,
            preference: preference.value as PreferenceOption,
            subscribe: subscribe.value,
            cityId: bestMatchForCityId,
            regionId: bestMatchForRegionId,
            token: token.value,
          },
        })
      : await sendMessage({
          body: {
            name: name.value,
            email: email.value,
            telephone: telephone.value,
            message: message.value,
            callback: callback.value as CallbackOption,
            preference: preference.value as PreferenceOption,
            subscribe: subscribe.value,
            cityId: bestMatchForCityId,
            regionId: bestMatchForRegionId,
            token: token.value,
          },
        });

    if (result.response?.ok) {
      showForm.value = false;
      return;
    }

    if (result.error && isValidationProblemDetails(result.error) && result.error.errors) {
      let hasValidationErrors = false;
      for (const key in result.error.errors) {
        if (!result.error.errors[key] || !result.error.errors[key][0]) continue;
        switch (key) {
          case 'name':
            name.valid = false;
            name.error = result.error.errors[key][0];
            hasValidationErrors = true;
            break;
          case 'email':
            email.valid = false;
            email.error = result.error.errors[key][0];
            hasValidationErrors = true;
            break;
          case 'telephone':
            telephone.valid = false;
            telephone.error = result.error.errors[key][0];
            hasValidationErrors = true;
            break;
          case 'message':
            message.valid = false;
            message.error = result.error.errors[key][0];
            hasValidationErrors = true;
            break;
          case 'callback':
            callback.valid = false;
            callback.error = result.error.errors[key][0];
            hasValidationErrors = true;
            break;
          case 'preference':
            preference.valid = false;
            preference.error = result.error.errors[key][0];
            hasValidationErrors = true;
            break;
          case 'subscribe':
            subscribe.valid = false;
            subscribe.error = result.error.errors[key][0];
            hasValidationErrors = true;
            break;
          case 'token':
            token.valid = false;
            token.error = result.error.errors[key][0];
            hasValidationErrors = true;
            break;
        }
      }

      if (hasValidationErrors) {
        resetTurnstile();
        return;
      }
    }

    resetTurnstile();
    responseError.value = t('components.contact-form.error');
  } catch {
    resetTurnstile();
    responseError.value = t('components.contact-form.error');
  } finally {
    isSubmitting.value = false;
  }
};

watch(() => name.value, validateName);
watch(() => email.value, validateEmail);
watch(() => telephone.value, validateTelephone);
watch(() => token.value, validateToken);
</script>

<template>
  <section class="no-print">
    <h2 v-if="!project" class="2xs:text-lg xs:text-xl mb-2 text-base font-semibold">{{ header ?? t('components.contact-form.header') }}</h2>
    <div :class="['overflow-hidden rounded-md border-2', project ? 'border-ph-green-200 bg-ph-green-50 shadow-ph-green-700/20 shadow-lg' : 'border-gray-200 bg-gray-50']">
      <!-- Sur une fiche projet, le titre entre dans la carte sur un bandeau vert foncé
           avec une phrase de réassurance : le bloc devient une unité visuelle forte. -->
      <div v-if="project" class="bg-ph-green-700 px-4 py-3 text-white">
        <h2 class="2xs:text-lg xs:text-xl text-base font-semibold"><FontAwesomeIcon :icon="faEnvelope" class="text-ph-green-200 mr-2" />{{ header ?? t('components.contact-form.header') }}</h2>
        <p class="text-ph-green-200 mt-0.5 text-xs">{{ t('components.contact-form.subheader') }}</p>
      </div>
      <form v-if="showForm" class="grid gap-4 p-4" @submit.prevent="handleSubmit">
        <div class="relative mt-1">
          <label class="absolute -top-2 left-2 inline-block rounded-lg bg-white px-1 text-xs font-medium text-gray-900" :for="fieldId('name')">{{ t('components.contact-form.name.label') }}</label>
          <input
            :id="fieldId('name')"
            v-model="name.value"
            :class="[
              !name.valid ? 'text-red-900 outline-red-300 focus:outline-red-600' : 'focus:outline-ph-green-600 text-gray-900 outline-gray-300',
              'block w-full rounded-md border-none bg-white px-3 py-1.5 text-base outline-1 -outline-offset-2 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 sm:text-sm/6',
            ]"
            :placeholder="t('components.contact-form.name.placeholder')"
            name="name"
            type="text"
            @keydown.enter.prevent
          />
          <div v-if="!name.valid" class="py-1 pl-2 text-xs text-red-900">{{ name.error }}</div>
        </div>
        <div class="relative">
          <label class="absolute -top-2 left-2 inline-block rounded-lg bg-white px-1 text-xs font-medium text-gray-900" :for="fieldId('email')">{{ t('components.contact-form.email.label') }}</label>
          <input
            :id="fieldId('email')"
            v-model="email.value"
            :class="[
              !email.valid ? 'text-red-900 outline-red-300 focus:outline-red-600' : 'focus:outline-ph-green-600 text-gray-900 outline-gray-300',
              'block w-full rounded-md border-none bg-white px-3 py-1.5 text-base outline-1 -outline-offset-2 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 sm:text-sm/6',
            ]"
            :placeholder="t('components.contact-form.email.placeholder')"
            name="email"
            type="text"
            @keydown.enter.prevent
          />
          <div v-if="!email.valid" class="py-1 pl-2 text-xs text-red-900">{{ email.error }}</div>
        </div>
        <div class="relative">
          <label class="absolute -top-2 left-2 inline-block rounded-lg bg-white px-1 text-xs font-medium text-gray-900" :for="fieldId('telephone')">{{ t('components.contact-form.telephone.label') }}</label>
          <input
            :id="fieldId('telephone')"
            v-model="telephone.value"
            :placeholder="t('components.contact-form.telephone.placeholder')"
            :class="[
              !telephone.valid ? 'text-red-900 outline-red-300 focus:outline-red-600' : 'focus:outline-ph-green-600 text-gray-900 outline-gray-300',
              'block w-full rounded-md border-none bg-white px-3 py-1.5 text-base outline-1 -outline-offset-2 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 sm:text-sm/6',
            ]"
            name="telephone"
            type="text"
            @keydown.enter.prevent
          />
          <div v-if="!telephone.valid" class="py-1 pl-2 text-xs text-red-900">{{ telephone.error }}</div>
        </div>
        <div class="relative">
          <label class="absolute -top-2 left-2 inline-block rounded-lg bg-white px-1 text-xs font-medium text-gray-900" :for="fieldId('message')">{{ t('components.contact-form.message.label') }}</label>
          <textarea
            :id="fieldId('message')"
            v-model="message.value"
            :placeholder="t('components.contact-form.message.placeholder')"
            class="focus:outline-ph-green-600 block w-full rounded-md border-none bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-2 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 sm:text-sm/6"
            name="message"
            rows="4"
          />
          <div v-if="!message.valid" class="py-1 pl-2 text-xs text-red-900">{{ message.error }}</div>
        </div>
        <!-- Les deux menus tiennent sur une ligne dès qu'il y a la place : ils servent surtout au
             vendeur et ne méritent pas deux rangées dans une colonne étroite. -->
        <div class="2xs:grid-cols-2 grid grid-cols-1 gap-4">
          <div class="relative">
            <label class="absolute -top-2 left-2 inline-block rounded-lg bg-white px-1 text-xs font-medium text-gray-900" :for="fieldId('callback')">{{ t('components.contact-form.callback.label') }}</label>
            <div class="grid grid-cols-1">
              <select
                :id="fieldId('callback')"
                v-model="callback.value"
                class="focus:outline-ph-green-600 col-start-1 row-start-1 w-full appearance-none rounded-md border-none bg-white py-1.5 pr-8 pl-3 text-base text-gray-900 outline-1 -outline-offset-2 outline-gray-300 focus:outline-2 focus:-outline-offset-2 sm:text-sm/6"
                name="callback"
              >
                <option selected value="asap">{{ t('components.contact-form.callback.asap') }}</option>
                <option value="tomorrow">{{ t('components.contact-form.callback.tomorrow') }}</option>
                <option value="this-week">{{ t('components.contact-form.callback.this-week') }}</option>
              </select>
              <ChevronDownIcon aria-hidden="true" class="pointer-events-none col-start-1 row-start-1 mr-2 size-5 self-center justify-self-end text-gray-500 sm:size-4" />
            </div>
            <div v-if="!callback.valid" class="py-1 pl-2 text-xs text-red-900">{{ callback.error }}</div>
          </div>
          <div class="relative">
            <label class="absolute -top-2 left-2 inline-block rounded-lg bg-white px-1 text-xs font-medium text-gray-900" :for="fieldId('preference')">{{ t('components.contact-form.preference.label') }}</label>
            <div class="grid grid-cols-1">
              <select
                :id="fieldId('preference')"
                v-model="preference.value"
                class="focus:outline-ph-green-600 col-start-1 row-start-1 w-full appearance-none rounded-md border-none bg-white py-1.5 pr-8 pl-3 text-base text-gray-900 outline-1 -outline-offset-2 outline-gray-300 focus:outline-2 focus:-outline-offset-2 sm:text-sm/6"
                name="preference"
              >
                <option selected value="anytime">{{ t('components.contact-form.preference.anytime') }}</option>
                <option value="morning">{{ t('components.contact-form.preference.morning') }}</option>
                <option value="day">{{ t('components.contact-form.preference.day') }}</option>
                <option value="night">{{ t('components.contact-form.preference.night') }}</option>
              </select>
              <ChevronDownIcon aria-hidden="true" class="pointer-events-none col-start-1 row-start-1 mr-2 size-5 self-center justify-self-end text-gray-500 sm:size-4" />
            </div>
            <div v-if="!preference.valid" class="py-1 pl-2 text-xs text-red-900">{{ preference.error }}</div>
          </div>
        </div>
        <div class="flex gap-3">
          <div class="flex h-6 shrink-0 items-center">
            <div class="group grid size-4 grid-cols-1">
              <input
                :id="fieldId('subscribe')"
                v-model="subscribe.value"
                class="checked:border-ph-green-600 checked:bg-ph-green-600 indeterminate:border-ph-green-600 indeterminate:bg-ph-green-600 focus-visible:outline-ph-green-600 col-start-1 row-start-1 appearance-none rounded-sm border border-gray-300 bg-white focus-visible:outline-2 focus-visible:outline-offset-2 disabled:border-gray-300 disabled:bg-gray-100 disabled:checked:bg-gray-100 forced-colors:appearance-auto"
                name="subscribe"
                type="checkbox"
              />
              <svg class="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white group-has-disabled:stroke-gray-950/25" fill="none" viewBox="0 0 14 14">
                <path class="opacity-0 group-has-checked:opacity-100" d="M3 8L6 11L11 3.5" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" />
                <path class="opacity-0 group-has-indeterminate:opacity-100" d="M3 7H11" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" />
              </svg>
            </div>
          </div>
          <div class="text-sm/6">
            <label class="text-gray-500" :for="fieldId('subscribe')">
              <span class="font-medium text-gray-900">{{ t('components.contact-form.subscribe.consent') }}</span> {{ t('components.contact-form.subscribe.subject') }}
            </label>
          </div>
          <div v-if="!subscribe.valid" class="py-1 pl-2 text-xs text-red-900">{{ subscribe.error }}</div>
        </div>
        <div class="flex flex-col">
          <ResponsiveTurnstileWidget ref="turnstileWidget" @error="handleTurnstileError" @expired="handleTurnstileExpired" @success="handleTurnstileSuccess" />
          <div v-if="!token.valid" class="py-1 pl-2 text-xs text-red-900">{{ token.error }}</div>
        </div>
        <div :class="project ? '' : 'text-right'">
          <button
            :disabled="isSubmitting"
            :class="[project ? 'block w-full' : 'inline-block', 'hover:bg-ph-green-600 bg-ph-green-700 cursor-pointer rounded-lg px-4 py-2 text-white disabled:cursor-not-allowed disabled:opacity-50']"
            type="submit"
          >
            <FontAwesomeIcon :icon="faPaperPlane" class="text-ph-green-200 mr-1" />
            {{ project ? t('components.contact-form.send-request') : t('components.contact-form.send') }}
          </button>
        </div>
        <p class="text-center text-xs text-gray-600">
          <span>{{ t('components.contact-form.privacy-consent') }}</span>
          <RouterLink :to="{ name: 'privacy' }" class="hover:text-ph-green-700 underline">
            {{ t('components.contact-form.privacy-policy') }}
          </RouterLink>
          <span>.</span>
        </p>
        <p v-if="responseError" class="text-center text-sm text-red-900">
          {{ responseError }}
        </p>
      </form>
      <div v-else class="flex flex-col items-center justify-center gap-6 p-10">
        <FontAwesomeIcon :icon="faEnvelope" class="text-ph-green-700" size="4x" />
        {{ t('components.contact-form.success-message') }}
      </div>
    </div>
  </section>
</template>

<style lang="css" scoped></style>
